import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { CommentaireEcriture, Ecriture } from '../../api/comptabilite';
import { useAuth } from '../../contexts/AuthContext';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

/**
 * Espace expert-comptable (TF-062) : lecture des écritures + fil de commentaires
 * par écriture. L'ADMIN peut inviter un expert externe (rôle EXPERT_COMPTABLE,
 * lecture seule) qui reçoit un e-mail pour définir son mot de passe.
 */
const ExpertComptable: React.FC = () => {
  const { user } = useAuth();
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [ouverte, setOuverte] = useState<number | null>(null);
  const [commentaires, setCommentaires] = useState<CommentaireEcriture[]>([]);
  const [texte, setTexte] = useState('');
  const [invite, setInvite] = useState({ email: '', nom: '', prenom: '' });
  const [busy, setBusy] = useState(false);

  const load = () =>
    comptabiliteService.expertEcritures(startDate, endDate).then(setEcritures)
      .catch((e: any) => { if (e?.response?.status !== 403) toast.error('Chargement impossible.'); });

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const ouvrir = async (id: number) => {
    if (ouverte === id) { setOuverte(null); return; }
    setOuverte(id);
    setTexte('');
    try { setCommentaires(await comptabiliteService.commentairesEcriture(id)); }
    catch { setCommentaires([]); }
  };

  const commenter = async () => {
    if (!ouverte || !texte.trim()) return;
    setBusy(true);
    try {
      await comptabiliteService.commenterEcriture(ouverte, texte.trim());
      setTexte('');
      setCommentaires(await comptabiliteService.commentairesEcriture(ouverte));
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Commentaire impossible.'); }
    finally { setBusy(false); }
  };

  const inviter = async () => {
    if (!invite.email.trim() || !invite.nom.trim()) { toast.error('E-mail et nom requis.'); return; }
    setBusy(true);
    try {
      const r = await comptabiliteService.inviterExpert({
        email: invite.email.trim(), nom: invite.nom.trim(), prenom: invite.prenom || undefined,
      });
      toast.success(r.dejaExistant
        ? 'Expert rattaché à la société — e-mail de connexion envoyé.'
        : 'Expert invité — il recevra un e-mail pour définir son mot de passe.');
      setInvite({ email: '', nom: '', prenom: '' });
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Invitation impossible.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Espace expert-comptable</h1>
        <p className="text-sm text-gray-500">
          Lecture des écritures et collaboration par commentaires.{' '}
          <Link to="/comptabilite" className="text-blue-600 hover:underline">← Comptabilité</Link>
        </p>
      </div>

      {user?.role === 'ADMIN' && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-3">
          <h2 className="text-lg font-semibold">Inviter un expert-comptable</h2>
          <p className="text-xs text-gray-500">
            L'expert accède en <strong>lecture seule</strong> à la comptabilité et peut commenter les écritures.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-52">
              <label className="block text-xs text-gray-600 mb-1">E-mail</label>
              <input type="email" value={invite.email}
                     onChange={(e) => setInvite((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Nom</label>
              <input value={invite.nom}
                     onChange={(e) => setInvite((f) => ({ ...f, nom: e.target.value }))} className={inputCls + ' w-40'} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Prénom</label>
              <input value={invite.prenom}
                     onChange={(e) => setInvite((f) => ({ ...f, prenom: e.target.value }))} className={inputCls + ' w-40'} />
            </div>
            <button onClick={inviter} disabled={busy}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
              Inviter
            </button>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="text-lg font-semibold flex-1">Écritures</h2>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Du</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Au</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
          </div>
          <button onClick={load}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
            Actualiser
          </button>
        </div>

        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr><th className="px-4 py-2">Numéro</th><th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Libellé</th><th className="px-4 py-2 text-right">Débit</th>
                <th className="px-4 py-2 text-right">Crédit</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ecritures.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Aucune écriture sur la période.</td></tr>
            )}
            {ecritures.map((e) => (
              <React.Fragment key={e.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{e.numero}</td>
                  <td className="px-4 py-2">{new Date(e.dateEcriture).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{e.libelle}</td>
                  <td className="px-4 py-2 text-right">{formatTND(e.totalDebit)}</td>
                  <td className="px-4 py-2 text-right">{formatTND(e.totalCredit)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => ouvrir(e.id)} className="text-blue-600 hover:underline">
                      {ouverte === e.id ? 'Fermer' : 'Commentaires'}
                    </button>
                  </td>
                </tr>
                {ouverte === e.id && (
                  <tr>
                    <td colSpan={6} className="px-4 py-3 bg-gray-50/60">
                      <div className="space-y-2">
                        {commentaires.length === 0 && (
                          <p className="text-xs text-gray-400">Aucun commentaire sur cette écriture.</p>
                        )}
                        {commentaires.map((c) => (
                          <div key={c.id} className="bg-white border border-gray-100 rounded-md p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">
                                {c.auteur}
                                {c.role === 'EXPERT_COMPTABLE' && (
                                  <span className="ml-2 text-[11px] font-semibold text-indigo-700 bg-indigo-50 rounded-full px-2 py-0.5">
                                    Expert-comptable
                                  </span>
                                )}
                              </span>
                              <span className="text-[11px] text-gray-400">{new Date(c.createdAt).toLocaleString()}</span>
                            </div>
                            <p className="text-gray-700 mt-1 whitespace-pre-line">{c.texte}</p>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input value={texte} onChange={(ev) => setTexte(ev.target.value)}
                                 placeholder="Votre commentaire…"
                                 className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                          <button onClick={commenter} disabled={busy || !texte.trim()}
                                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                            Commenter
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpertComptable;
